import { zodResolver } from '@hookform/resolvers/zod';
import WarningAmberRounded from '@mui/icons-material/WarningAmberRounded';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import type { ReactNode } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

export const reasonSchema = z.object({
  reason: z.string().trim().min(3, 'Give a short reason (at least 3 characters)').max(500),
});

/**
 * Confirmation for an irreversible action that needs a reason (void, cancel). Like ConfirmDialog,
 * but with a required reason field and room for a financial-effect summary.
 */
export function ReasonDialog({
  open,
  title,
  body,
  confirmLabel,
  danger = true,
  onConfirm,
  onClose,
}: {
  open: boolean;
  title: string;
  body: ReactNode;
  confirmLabel: string;
  danger?: boolean;
  onConfirm: (reason: string) => Promise<unknown>;
  onClose: () => void;
}) {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({ resolver: zodResolver(reasonSchema), defaultValues: { reason: '' } });

  const close = () => {
    if (isSubmitting) return;
    reset();
    onClose();
  };

  return (
    <Dialog open={open} onClose={close} maxWidth="xs" fullWidth>
      <form
        noValidate
        onSubmit={(e) =>
          void handleSubmit(async ({ reason }) => {
            try {
              await onConfirm(reason);
              reset();
              onClose();
            } catch {
              /* the caller shows the error; keep the dialog open */
            }
          })(e)
        }
      >
        <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>
          <Stack direction="row" spacing={1.25} sx={{ alignItems: 'center' }}>
            {danger && (
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: '10px',
                  display: 'grid',
                  placeItems: 'center',
                  color: 'error.main',
                  bgcolor: 'color-mix(in srgb, var(--mui-palette-error-main) 10%, transparent)',
                }}
              >
                <WarningAmberRounded fontSize="small" />
              </Box>
            )}
            <span>{title}</span>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Typography component="div" color="text.secondary" variant="body2" sx={{ mb: 2.5 }}>
            {body}
          </Typography>
          <TextField
            {...register('reason')}
            label="Reason"
            autoFocus
            fullWidth
            multiline
            minRows={2}
            error={Boolean(errors.reason)}
            helperText={errors.reason?.message ?? 'Kept in the audit trail.'}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button variant="outlined" onClick={close} disabled={isSubmitting}>
            Keep it
          </Button>
          <Button
            type="submit"
            variant="contained"
            color={danger ? 'error' : 'primary'}
            loading={isSubmitting}
          >
            {confirmLabel}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
